const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || '/api'}/chat/messages`;

class ChatMessagesAPI {
  // Create a new chat message
  async createMessage(role, content, chatSessionId, streams = []) {
    const response = await fetch(`${API_BASE_URL}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        role,
        content,
        chat_session_id: chatSessionId,
        streams
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create chat message');
    }
    
    return response.json();
  }

  // Get a specific chat message by ID
  async getMessage(messageId) {
    const response = await fetch(`${API_BASE_URL}/${messageId}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch chat message');
    }
    
    return response.json();
  }

  // Update a chat message
  async updateMessage(messageId, role, content, streams) {
    const response = await fetch(`${API_BASE_URL}/${messageId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        role,
        content,
        streams
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update chat message');
    }
    
    return response.json();
  }

  // Delete a chat message
  async deleteMessage(messageId) {
    const response = await fetch(`${API_BASE_URL}/${messageId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete chat message');
    }
    
    return response.ok;
  }

  // Get messages by session
  async getSessionMessages(sessionId, limit = 100, offset = 0) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch session messages');
    }
    
    return response.json();
  }

  // Get conversation history
  async getConversationHistory(sessionId, limit = 100) {
    const params = new URLSearchParams({
      limit: limit.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/conversation?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch conversation history');
    }
    
    return response.json();
  }

  // Get recent messages
  async getRecentMessages(sessionId, limit = 50) {
    const params = new URLSearchParams({
      limit: limit.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/recent?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch recent messages');
    }
    
    return response.json();
  }

  // Get messages by role
  async getMessagesByRole(sessionId, role) {
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/role/${role}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch messages by role');
    }
    
    return response.json();
  }

  // Get last message
  async getLastMessage(sessionId) {
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/last`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch last message');
    }
    
    return response.json();
  }

  // Get messages with streams
  async getMessagesWithStreams(sessionId) {
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/with-streams`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch messages with streams');
    }
    
    return response.json();
  }

  // Search messages by content
  async searchMessages(sessionId, searchTerm) {
    const params = new URLSearchParams({
      search_term: searchTerm
    });
    
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/search?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to search messages');
    }
    
    return response.json();
  }

  // Get messages by date range
  async getMessagesByDateRange(sessionId, startDate, endDate) {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });
    
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/date-range?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch messages by date range');
    }
    
    return response.json();
  }

  // Delete all session messages
  async deleteAllSessionMessages(sessionId) {
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/all`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete all session messages');
    }
    
    return response.json();
  }

  // Get message count
  async getMessageCount(sessionId) {
    const response = await fetch(`${API_BASE_URL}/session/${sessionId}/count`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch message count');
    }
    
    return response.json();
  }

  // Helper method to get streams content in order
  getStreamsContent(streams) {
    if (!streams || !Array.isArray(streams)) return '';
    
    // Sort streams by created_at to maintain order
    const sortedStreams = [...streams].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    
    return sortedStreams.map(stream => stream.content || '').join('');
  }

  // Helper method to format message for display
  formatMessageForDisplay(message) {
    return {
      id: message.id,
      role: message.role,
      content: message.content,
      streams: message.streams || [],
      created_at: message.created_at,
      chat_session_id: message.chat_session_id
    };
  }
}

export default new ChatMessagesAPI();
